import React, { useState } from 'react';
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  List,
  Typography,
  IconButton,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard,
  People,
  Store,
  Business,
  AccountCircle,
  Logout,
  AccessTime,
  Settings,
  EventNote,
  VideoCall,
  Schedule,
  LocalShipping,
  Thermostat,
  EmojiEvents,
  Campaign,
  ContactMail,
  Storefront,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { ROUTES } from '../../constants';
import Logo from '../common/Logo';
import ProfileAvatar from '../common/ProfileAvatar';

const drawerWidth = 240;

interface LayoutProps {
  children: React.ReactNode;
  onAddUser?: () => void;
  searchValue?: string;
  onSearchChange?: (value: string) => void;
}

interface NavigationItem {
  text: string;
  icon: React.ReactNode;
  path: string;
  roles?: string[];
}

const menuItems: NavigationItem[] = [
  {
    text: 'Dashboard',
    icon: <Dashboard />,
    path: ROUTES.DASHBOARD,
    roles: ['super_admin', 'cluster_level_manager'],
  },
  {
    text: 'Users',
    icon: <People />,
    path: ROUTES.USERS,
  },
  {
    text: 'Work Timings',
    icon: <AccessTime />,
    path: ROUTES.WORK_TIMINGS,
    roles: ['super_admin', 'cluster_level_manager', 'store_manager'],
  },
  {
    text: 'Stores',
    icon: <Store />,
    path: ROUTES.STORES,
    roles: ['super_admin', 'cluster_level_manager'],
  },
  {
    text: 'Store Manager Management',
    icon: <People />,
    path: ROUTES.STORE_MANAGER_MANAGEMENT,
    roles: ['super_admin', 'cluster_level_manager'],
  },
  {
    text: 'Clusters',
    icon: <Business />,
    path: ROUTES.CLUSTERS,
    roles: ['super_admin', 'cluster_level_manager'],
  },
  {
    text: 'Shifts',
    icon: <Schedule />,
    path: ROUTES.SHIFTS,
    roles: ['super_admin', 'cluster_level_manager'],
  },
  {
    text: 'Attendance',
    icon: <AccessTime />,
    path: ROUTES.ATTENDANCE,
    roles: ['super_admin', 'cluster_level_manager', 'store_manager', 'mis'],
  },
  {
    text: 'Leaves',
    icon: <EventNote />,
    path: ROUTES.LEAVES,
  },
  {
    text: 'Visitors',
    icon: <People />,
    path: ROUTES.VISITORS,
    roles: ['super_admin', 'cluster_level_manager', 'store_manager', 'security_staff'],
  },
  {
    text: 'Meetings',
    icon: <VideoCall />,
    path: ROUTES.MEETINGS,
    roles: ['super_admin', 'cluster_level_manager'],
  },
  {
    text: 'Vehicle Captures',
    icon: <LocalShipping />,
    path: ROUTES.VEHICLE_CAPTURES,
    roles: ['super_admin', 'cluster_level_manager', 'store_manager', 'security_staff'],
  },
  {
    text: 'Temperature Captures',
    icon: <Thermostat />,
    path: ROUTES.TEMPERATURE_CAPTURES,
    roles: ['super_admin', 'cluster_level_manager', 'store_manager', 'security_staff'],
  },
  {
    text: 'Performers',
    icon: <EmojiEvents />,
    path: ROUTES.PERFORMERS,
    roles: ['super_admin', 'cluster_level_manager'],
  },
  {
    text: 'Announcements',
    icon: <Campaign />,
    path: ROUTES.ANNOUNCEMENTS,
    roles: ['super_admin', 'cluster_level_manager'],
  },
  {
    text: 'Contact Inquiries',
    icon: <ContactMail />,
    path: ROUTES.CONTACTS,
    roles: ['super_admin', 'cluster_level_manager'],
  },
  {
    text: 'Vendors',
    icon: <Storefront />,
    path: ROUTES.VENDORS,
    roles: ['super_admin', 'cluster_level_manager'],
  },
  {
    text: 'Settings',
    icon: <Settings />,
    path: ROUTES.SETTINGS,
    roles: ['super_admin', 'cluster_level_manager'],
  },
];

const Layout: React.FC<LayoutProps> = ({ children, onAddUser, searchValue, onSearchChange }) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = async () => {
    handleMenuClose();
    await logout();
  };

  const handleNavigation = (path: string) => {
    navigate(path);
    setMobileOpen(false);
  };

  const drawer = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Logo Section */}
      <Box sx={{ p: 3, borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}>
        <Logo size="medium" color="#ffffff" />
      </Box>
      
      {/* Navigation Menu */}
      <Box sx={{ flex: 1, py: 2 }}>
        <List>
          {menuItems.map((item) => {
            // For MIS users, only show Attendance menu item
            if (user?.role === 'mis' && item.path !== ROUTES.ATTENDANCE) {
              return null;
            }
            
            // Check if user has access to this menu item
            if (item.roles && user && !item.roles.includes(user.role)) {
              return null;
            }

            const isActive = location.pathname === item.path;
            
            return (
              <ListItem key={item.text} disablePadding sx={{ px: 2 }}>
                <ListItemButton
                  onClick={() => handleNavigation(item.path)}
                  selected={isActive}
                  sx={{
                    borderRadius: 2,
                    mb: 0.5,
                    '&.Mui-selected': {
                      backgroundColor: 'rgba(33, 150, 243, 0.15)',
                      color: '#2196f3',
                      '& .MuiListItemIcon-root': {
                        color: '#2196f3',
                      },
                      '&:hover': {
                        backgroundColor: 'rgba(33, 150, 243, 0.2)',
                      },
                    },
                    '&:hover': {
                      backgroundColor: 'rgba(255, 255, 255, 0.05)',
                    },
                  }}
                >
                  <ListItemIcon sx={{ color: isActive ? '#2196f3' : 'rgba(255, 255, 255, 0.7)' }}>
                    {item.icon}
                  </ListItemIcon>
                  <ListItemText 
                    primary={item.text} 
                    sx={{ 
                      color: isActive ? '#2196f3' : 'rgba(255, 255, 255, 0.9)',
                      '& .MuiTypography-root': {
                        fontWeight: isActive ? 600 : 400,
                      }
                    }} 
                  />
                </ListItemButton>
              </ListItem>
            );
          })}
        </List>
      </Box>
      
      {/* User Info at Bottom */}
      <Box sx={{ p: 2, borderTop: '1px solid rgba(255, 255, 255, 0.1)' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <ProfileAvatar
            imageUrl={user?.profile_image_url}
            firstName={user?.first_name}
            lastName={user?.last_name}
            sx={{ bgcolor: '#2196f3', width: 32, height: 32 }}
          />
          <Box>
            <Typography variant="body2" sx={{ color: '#ffffff', fontWeight: 500 }}>
              {user?.first_name} {user?.last_name}
            </Typography>
            <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.6)' }}>
              {user?.role?.replace('_', ' ').toUpperCase()}
            </Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      {/* Top App Bar */}
      <AppBar
        position="fixed"
        sx={{
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          ml: { sm: `${drawerWidth}px` },
          backgroundColor: '#ffffff',
          color: '#333333',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { sm: 'none' }, color: '#333333' }}
          >
            <MenuIcon />
          </IconButton>
          <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1, color: '#333333', fontWeight: 600 }}>
            {menuItems.find(item => item.path === location.pathname)?.text || 'Dashboard'}
          </Typography>
          
          
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="body2" sx={{ color: '#666666' }}>
              {user?.first_name} {user?.last_name}
            </Typography>
            <IconButton
              size="large"
              aria-label="account of current user"
              aria-controls="menu-appbar"
              aria-haspopup="true"
              onClick={handleMenuOpen}
              sx={{ color: '#333333' }}
            >
              <AccountCircle />
            </IconButton>
            <Menu
              id="menu-appbar"
              anchorEl={anchorEl}
              anchorOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              keepMounted
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              open={Boolean(anchorEl)}
              onClose={handleMenuClose}
            >
              <MenuItem onClick={() => { handleMenuClose(); navigate(ROUTES.PROFILE); }}>
                <ListItemIcon>
                  <AccountCircle fontSize="small" />
                </ListItemIcon>
                Profile
              </MenuItem>
              <MenuItem onClick={handleLogout}>
                <ListItemIcon>
                  <Logout fontSize="small" />
                </ListItemIcon>
                Logout
              </MenuItem>
            </Menu>
          </Box>
        </Toolbar>
      </AppBar>
      
      {/* Sidebar */}
      <Box
        component="nav"
        sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}
        aria-label="mailbox folders"
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': { 
              boxSizing: 'border-box', 
              width: drawerWidth,
              backgroundColor: '#081122',
              borderRadius: 0,
            },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': { 
              boxSizing: 'border-box', 
              width: drawerWidth,
              backgroundColor: '#081122',
              borderRight: 'none',
              borderRadius: 0,
            },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>
      
      {/* Main Content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          backgroundColor: '#f5f5f5',
          minHeight: '100vh',
        }}
      >
        <Toolbar />
        {children}
      </Box>
    </Box>
  );
};

export default Layout;
